import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '@/context/AppContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ShieldAlert, Upload, History, Database, Archive, ScrollText, Loader2,
  CheckCircle2, XCircle, AlertTriangle, Download, FileSpreadsheet, Receipt,
  ChevronDown, ChevronRight, PlusCircle, RefreshCw,
} from 'lucide-react';

const money = (n) => `\u20b9${Number(n || 0).toLocaleString('en-IN')}`;

export default function DataManagement() {
  const { user, stores } = useApp();
  const isOwner = user && (user.role === 'OWNER' || user.owner_access);

  if (!isOwner) {
    return (
      <div className="mx-auto max-w-lg p-6" data-testid="dm-access-denied">
        <Alert variant="destructive">
          <ShieldAlert className="h-5 w-5" />
          <AlertTitle>Owner access only</AlertTitle>
          <AlertDescription>
            This Data Management area is restricted to the Owner. Your role does not have access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-4 md:p-6 space-y-6" data-testid="data-management-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Data Management</h1>
        <p className="text-sm text-muted-foreground">Owner-only tools for import, store data and recovery.</p>
      </div>
      <Tabs defaultValue="import">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="import" data-testid="tab-import"><Upload className="mr-1 h-4 w-4" />Import Items</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices"><Receipt className="mr-1 h-4 w-4" />Import Invoices</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><History className="mr-1 h-4 w-4" />Import History</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review"><PlusCircle className="mr-1 h-4 w-4" />Review Costs</TabsTrigger>
          <TabsTrigger value="store" data-testid="tab-store"><Database className="mr-1 h-4 w-4" />Store Data</TabsTrigger>
          <TabsTrigger value="backups" data-testid="tab-backups"><Archive className="mr-1 h-4 w-4" />Backups</TabsTrigger>
          <TabsTrigger value="export" data-testid="tab-export"><Download className="mr-1 h-4 w-4" />Export</TabsTrigger>
          <TabsTrigger value="historical" data-testid="tab-historical"><History className="mr-1 h-4 w-4" />Historical</TabsTrigger>
          <TabsTrigger value="incentives" data-testid="tab-incentives"><ScrollText className="mr-1 h-4 w-4" />Incentives</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="mr-1 h-4 w-4" />Audit Log</TabsTrigger>
        </TabsList>
        <TabsContent value="import"><ImportExcel stores={stores} /></TabsContent>
        <TabsContent value="invoices"><ImportInvoices stores={stores} /></TabsContent>
        <TabsContent value="history"><ImportHistory /></TabsContent>
        <TabsContent value="review"><ReviewCosts /></TabsContent>
        <TabsContent value="store"><StoreData stores={stores} /></TabsContent>
        <TabsContent value="backups"><Backups /></TabsContent>
        <TabsContent value="export"><ExportData stores={stores} /></TabsContent>
        <TabsContent value="historical"><HistoricalData stores={stores} /></TabsContent>
        <TabsContent value="incentives"><IncentiveRules /></TabsContent>
        <TabsContent value="audit"><AuditLog /></TabsContent>
      </Tabs>
    </div>
  );
}

const EXPORT_SETS = [
  { key: 'sales', label: 'Sales' },
  { key: 'stock', label: 'Stock / Inventory' },
  { key: 'purchases', label: 'Purchases' },
  { key: 'returns', label: 'Returns' },
  { key: 'transfers', label: 'Transfers' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'customers', label: 'Customers' },
  { key: 'suppliers', label: 'Suppliers' },
  { key: 'audit', label: 'Audit Log' },
];

function ExportData({ stores }) {
  const [scope, setScope] = useState('all');
  const [busy, setBusy] = useState('');

  const download = async (key) => {
    setBusy(key);
    try {
      const params = scope !== 'all' ? { store_id: scope } : {};
      const res = await api.get(`/owner/export/${key}`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `shabz_${key}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${key} exported`);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setBusy('');
    }
  };

  return (
    <Card data-testid="export-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Download className="h-5 w-5" />Export Reports (CSV)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">Owner-only. Exports are read-only and never change your data.</p>
        <div className="max-w-xs">
          <Label>Store</Label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="mt-1" data-testid="export-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stores</SelectItem>
              {stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {EXPORT_SETS.map((e) => (
            <Button key={e.key} variant="outline" data-testid={`export-${e.key}`}
              disabled={busy === e.key} onClick={() => download(e.key)}
              className="justify-start">
              {busy === e.key ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              {e.label}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


function HistoricalData({ stores }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [file, setFile] = useState(null);

  const load = useCallback(async () => {
    try { setRows((await api.get('/owner/historical')).data.data); } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/owner/historical/import?commit=false', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data.data);
    } catch (e) { toast.error('Could not read file'); } finally { setBusy(false); }
  };
  const doCommit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/owner/historical/import?commit=true', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.data.committed) { toast.success(`Imported ${res.data.data.valid_rows} historical rows`); setPreview(null); setFile(null); load(); }
      else toast.error(`Fix ${res.data.data.error_count} errors first`);
    } catch (e) { toast.error('Import failed'); } finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/owner/historical/${id}`); load(); };
  const snm = (id) => (stores.find((s) => s.id === id) || {}).name || id;

  const [erow, setErow] = useState(null); // {id?, year, month, store, sales, cogs, expenses, purchases}
  const blank = { year: new Date().getFullYear(), month: 1, store: stores[0]?.id || '', sales: 0, cogs: 0, expenses: 0, purchases: 0 };
  const saveRow = async () => {
    try {
      if (erow.id) await api.put(`/owner/historical/${erow.id}`, erow);
      else await api.post('/owner/historical', erow);
      toast.success('Saved'); setErow(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  return (
    <Card data-testid="historical-panel">
      <CardHeader><CardTitle className="flex items-center gap-2"><History className="h-5 w-5" />Historical / Manual Business Data</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <Alert><AlertTitle>How it works</AlertTitle><AlertDescription>
          Add rows manually or upload a CSV/XLSX with columns: <b>year, month, store, sales, cogs, expenses, net, purchases</b>. Rows are stored as HISTORICAL and combined with live Billing in dashboard reports (no double counting). Same year/month/store updates that row.
        </AlertDescription></Alert>

        <div className="flex flex-wrap items-end gap-3">
          <div><Label>File (CSV / XLSX)</Label>
            <Input type="file" accept=".csv,.xlsx,.xls" data-testid="hist-file"
              onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} className="mt-1" />
          </div>
          <Button variant="outline" onClick={doPreview} disabled={!file || busy} data-testid="hist-preview">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Preview</Button>
          <Button onClick={doCommit} disabled={!file || busy || (preview && preview.error_count > 0)} data-testid="hist-commit">Import File</Button>
          <Button variant="secondary" onClick={() => setErow(blank)} data-testid="hist-add-row">+ Add Row</Button>
        </div>
        {preview && (
          <div className="rounded-xl border border-border p-3 text-sm" data-testid="hist-preview-result">
            <p><b>{preview.valid_rows}</b> valid · <b className={preview.error_count ? 'text-destructive' : ''}>{preview.error_count}</b> errors</p>
            {preview.error_count > 0 && <ul className="mt-1 text-xs text-destructive">{preview.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}</ul>}
          </div>
        )}

        {erow && (
          <div className="grid grid-cols-2 gap-3 rounded-xl border border-primary/40 bg-primary/5 p-3 md:grid-cols-4" data-testid="hist-editor">
            <div><Label>Year</Label><Input type="number" value={erow.year} onChange={(e) => setErow({ ...erow, year: e.target.value })} data-testid="hr-year" /></div>
            <div><Label>Month (1-12)</Label><Input type="number" min="1" max="12" value={erow.month} onChange={(e) => setErow({ ...erow, month: e.target.value })} data-testid="hr-month" /></div>
            <div><Label>Store</Label>
              <Select value={erow.store} onValueChange={(v) => setErow({ ...erow, store: v })}>
                <SelectTrigger data-testid="hr-store"><SelectValue placeholder="Store" /></SelectTrigger>
                <SelectContent>{stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label>Sales</Label><Input type="number" value={erow.sales} onChange={(e) => setErow({ ...erow, sales: e.target.value })} data-testid="hr-sales" /></div>
            <div><Label>COGS</Label><Input type="number" value={erow.cogs} onChange={(e) => setErow({ ...erow, cogs: e.target.value })} data-testid="hr-cogs" /></div>
            <div><Label>Expenses</Label><Input type="number" value={erow.expenses} onChange={(e) => setErow({ ...erow, expenses: e.target.value })} data-testid="hr-expenses" /></div>
            <div><Label>Purchases</Label><Input type="number" value={erow.purchases} onChange={(e) => setErow({ ...erow, purchases: e.target.value })} data-testid="hr-purchases" /></div>
            <div className="flex items-end gap-2">
              <Button onClick={saveRow} data-testid="hr-save">Save</Button>
              <Button variant="ghost" onClick={() => setErow(null)}>Cancel</Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="hist-list">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Year</th><th>Month</th><th>Store</th><th>Sales</th><th>COGS</th><th>Expenses</th><th>Net</th><th></th></tr></thead>
            <tbody>
              {rows.length === 0 ? (<tr><td colSpan={8} className="py-5 text-center text-muted-foreground">No historical rows yet.</td></tr>) :
                rows.map((r) => (<tr key={r.id} className="border-t border-border"><td className="py-2">{r.year}</td><td>{r.month}</td><td>{snm(r.store_id)}</td><td className="font-mono">{r.sales}</td><td className="font-mono">{r.cogs}</td><td className="font-mono">{r.expenses}</td><td className="font-mono">{r.net}</td><td className="flex gap-1">
                  <Button size="sm" variant="ghost" data-testid={`hist-edit-${r.id}`} onClick={() => setErow({ id: r.id, year: r.year, month: r.month, store: r.store_id, sales: r.sales, cogs: r.cogs, expenses: r.expenses, purchases: r.purchases })}><CheckCircle2 className="h-4 w-4 text-primary" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(r.id)} data-testid={`hist-del-${r.id}`}><XCircle className="h-4 w-4 text-destructive" /></Button>
                </td></tr>))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function IncentiveRules() {
  const [cfg, setCfg] = useState({ default_pct: 0, targets: {}, pct: {} });
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const c = (await api.get('/reports/incentive-config')).data.data; setCfg({ default_pct: c.default_pct || 0, targets: c.targets || {}, pct: c.pct || {} });
        const u = (await api.get('/users')).data.data || [];
        setStaff(u.filter((x) => ['STAFF', 'MANAGER', 'OWNER'].includes(x.role)).map((x) => x.username));
      } catch (e) { /* */ }
    })();
  }, []);
  const save = async () => {
    setBusy(true);
    try { await api.post('/reports/incentive-config', cfg); toast.success('Incentive rules saved'); }
    catch (e) { toast.error('Save failed (Owner only)'); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="incentive-panel">
      <CardHeader><CardTitle className="flex items-center gap-2"><ScrollText className="h-5 w-5" />Staff Incentive Rules</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-xs"><Label>Default incentive %</Label>
          <Input type="number" step="0.1" value={cfg.default_pct} data-testid="inc-default-pct"
            onChange={(e) => setCfg({ ...cfg, default_pct: parseFloat(e.target.value) || 0 })} className="mt-1" />
        </div>
        <p className="text-sm text-muted-foreground">Per-staff target & % (leave % blank to use default). Earned/pending calculate automatically on the dashboard.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Staff</th><th>Monthly Target</th><th>Incentive %</th></tr></thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s} className="border-t border-border">
                  <td className="py-2 font-medium">{s}</td>
                  <td><Input type="number" className="h-9 w-32" data-testid={`inc-target-${s}`} value={cfg.targets[s] ?? ''} onChange={(e) => setCfg({ ...cfg, targets: { ...cfg.targets, [s]: parseFloat(e.target.value) || 0 } })} /></td>
                  <td><Input type="number" step="0.1" className="h-9 w-24" data-testid={`inc-pct-${s}`} value={cfg.pct[s] ?? ''} onChange={(e) => setCfg({ ...cfg, pct: { ...cfg.pct, [s]: parseFloat(e.target.value) || 0 } })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button onClick={save} disabled={busy} data-testid="inc-save">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Save Incentive Rules</Button>
      </CardContent>
    </Card>
  );
}


function ImportExcel({ stores }) {
  const [storeId, setStoreId] = useState('');
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [update, setUpdate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [mapping, setMapping] = useState({});

  const MAP_FIELDS = ['barcode', 'product', 'category', 'size', 'colour', 'fabric', 'cost', 'price'];

  const validate = async () => {
    if (!storeId) return toast.error('Select a target store first.');
    if (!file) return toast.error('Choose an Excel/CSV file.');
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const cleanMap = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v && v !== '__auto__'));
      if (Object.keys(cleanMap).length) fd.append('mapping', JSON.stringify(cleanMap));
      const r = await api.post(`/owner/import/validate?store_id=${storeId}`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setSummary(r.data.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Validation failed');
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!summary || summary.error_count > 0) return;
    setBusy(true);
    try {
      const r = await api.post('/owner/import/commit', {
        rows: summary.rows, store_id: storeId, update, filename: summary.filename,
      });
      setResult(r.data.data);
      toast.success(`Imported: ${r.data.data.inserted} inserted, ${r.data.data.skipped} skipped`);
      setSummary(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  const hasErrors = summary && summary.error_count > 0;

  return (
    <Card className="mt-4" data-testid="import-excel-card">
      <CardHeader><CardTitle>Import Excel</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Target store</Label>
            <Select value={storeId} onValueChange={setStoreId}>
              <SelectTrigger data-testid="import-store-select"><SelectValue placeholder="Select store" /></SelectTrigger>
              <SelectContent>
                {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>File (.xlsx, .xls, .csv)</Label>
            <Input type="file" accept=".xlsx,.xls,.csv" data-testid="import-file-input"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setSummary(null); setResult(null); }} />
          </div>
        </div>
        <Button onClick={validate} disabled={busy} data-testid="import-validate-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Validate file
        </Button>

        {summary && (
          <div className="space-y-3 rounded-lg border p-4" data-testid="import-summary">
            {summary.columns && summary.columns.length > 0 && (
              <details className="rounded-md border bg-muted/30 p-3" data-testid="import-mapping">
                <summary className="cursor-pointer text-sm font-medium">Column mapping (optional) — map your headers to fields</summary>
                <div className="mt-3 grid gap-2 md:grid-cols-2">
                  {MAP_FIELDS.map((f) => (
                    <div key={f} className="flex items-center gap-2">
                      <Label className="w-20 text-xs capitalize">{f}</Label>
                      <Select value={mapping[f] || '__auto__'} onValueChange={(v) => setMapping((m) => ({ ...m, [f]: v }))}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`map-${f}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__auto__">Auto-detect</SelectItem>
                          {summary.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" className="mt-2" onClick={validate} disabled={busy} data-testid="import-remap-btn">Re-validate with mapping</Button>
              </details>
            )}
            <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
              <Stat label="Rows" value={summary.total_rows} />
              <Stat label="Valid" value={summary.valid_rows} />
              <Stat label="To insert" value={summary.to_insert} />
              <Stat label="Already in DB" value={summary.already_in_db} />
              <Stat label="Duplicates in file" value={summary.duplicate_barcodes_in_file.length} />
              <Stat label="Errors" value={summary.error_count} />
              <Stat label="Est. retail" value={money(summary.est_retail_value)} />
              <Stat label="Est. cost" value={money(summary.est_cost_value)} />
            </div>
            <div className="text-sm">Store: <b>{summary.store_name}</b></div>
            {hasErrors ? (
              <Alert variant="destructive" data-testid="import-errors">
                <XCircle className="h-5 w-5" />
                <AlertTitle>Validation failed — nothing will be imported</AlertTitle>
                <AlertDescription>
                  <ul className="mt-1 max-h-40 list-disc space-y-0.5 overflow-auto pl-5 text-xs">
                    {summary.errors.map((er, i) => (
                      <li key={i}>Row {er.row}{er.barcode ? ` (${er.barcode})` : ''}: {er.error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            ) : (
              <Alert data-testid="import-ok">
                <CheckCircle2 className="h-5 w-5" />
                <AlertTitle>File is valid</AlertTitle>
                <AlertDescription>Ready to import {summary.to_insert} new garments into {summary.store_name}.</AlertDescription>
              </Alert>
            )}
            <div className="flex items-center gap-2">
              <Checkbox id="upd" checked={update} onCheckedChange={setUpdate} data-testid="import-update-checkbox" />
              <Label htmlFor="upd" className="text-sm">Update existing barcodes (product/price/cost) — otherwise skipped</Label>
            </div>
            <Button onClick={commit} disabled={busy || hasErrors} data-testid="import-commit-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Import now
            </Button>
          </div>
        )}

        {result && (
          <Alert data-testid="import-result">
            <CheckCircle2 className="h-5 w-5" />
            <AlertTitle>Import complete</AlertTitle>
            <AlertDescription>
              Inserted {result.inserted} · Skipped {result.skipped} · Updated {result.updated} · Errors {result.errors} ·
              Final store stock: <b>{result.final_store_stock}</b>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-md bg-muted/50 p-2">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="font-semibold">{value}</div>
  </div>
);

// Review Costs: bulk-fill the purchase cost for auto-created / needs-review items.
// Shows a smart suggested cost (median of similar priced items) you can apply.
function ReviewCosts() {
  const [rows, setRows] = useState(null);
  const [costs, setCosts] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const r = await api.get('/owner/garments/needs-review');
      const data = r.data.data || [];
      setRows(data);
      setCosts({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load items');
      setRows([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setCost = (bc, v) => setCosts((p) => ({ ...p, [bc]: v }));
  const applyAllSuggestions = () => {
    const next = { ...costs };
    (rows || []).forEach((g) => { if (g.suggested_cost != null && (next[g.barcode] == null || next[g.barcode] === '')) next[g.barcode] = g.suggested_cost; });
    setCosts(next);
    toast.success('Filled inputs with suggested costs where available');
  };

  const filledCount = (rows || []).filter((g) => costs[g.barcode] !== undefined && costs[g.barcode] !== '' && Number(costs[g.barcode]) >= 0).length;

  const saveAll = async () => {
    const items = (rows || [])
      .filter((g) => costs[g.barcode] !== undefined && costs[g.barcode] !== '' && Number(costs[g.barcode]) >= 0)
      .map((g) => ({ barcode: g.barcode, cost: Number(costs[g.barcode]) }));
    if (items.length === 0) { toast.info('Enter at least one cost first'); return; }
    setBusy(true);
    try {
      const r = await api.post('/owner/garments/bulk-cost', { items });
      toast.success(`Saved cost for ${r.data.data.updated} item(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="review-costs-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><PlusCircle className="h-5 w-5" />Review Costs</CardTitle>
        <CardDescription>
          Items with an unknown purchase cost (usually created automatically during invoice import).
          Fill the cost so your profit numbers are accurate. Your Sales totals are never affected.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="review-refresh-btn">
            <RefreshCw className="mr-2 h-4 w-4" />Refresh
          </Button>
          <Button size="sm" variant="secondary" onClick={applyAllSuggestions} disabled={busy || !rows?.length} data-testid="review-apply-all-btn">
            Apply all suggestions
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground" data-testid="review-filled-count">{filledCount} ready to save</span>
            <Button size="sm" onClick={saveAll} disabled={busy || filledCount === 0} data-testid="review-save-all-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Save All
            </Button>
          </div>
        </div>

        {rows === null ? (
          <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-8 text-center" data-testid="review-empty">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            <p className="text-sm font-medium">All caught up</p>
            <p className="text-xs text-muted-foreground">No items are waiting for a cost right now.</p>
          </div>
        ) : (
          <div className="-mx-4 overflow-x-auto px-4">
            <table className="min-w-[720px] w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="review-table">
              <thead className="sticky top-0 bg-muted/60 text-left text-xs">
                <tr>
                  <th className="py-2 font-medium">Barcode</th>
                  <th className="py-2 font-medium">Product</th>
                  <th className="py-2 font-medium">Store</th>
                  <th className="py-2 font-medium">Sold price</th>
                  <th className="py-2 font-medium">Suggested cost</th>
                  <th className="py-2 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((g) => (
                  <tr key={g.barcode} className="border-t" data-testid={`review-row-${g.barcode}`}>
                    <td className="py-1 font-mono text-xs">{g.barcode}</td>
                    <td className="py-1">{g.product || '-'}</td>
                    <td className="py-1 text-muted-foreground">{g.store_name || '-'}</td>
                    <td className="py-1">{g.price != null ? money(g.price) : '-'}</td>
                    <td className="py-1">
                      {g.suggested_cost != null ? (
                        <button type="button" onClick={() => setCost(g.barcode, g.suggested_cost)}
                          className="rounded-md border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700 hover:bg-sky-100"
                          title={`Based on ${g.suggested_basis || 'similar items'} - click to use`}
                          data-testid={`review-suggest-${g.barcode}`}>
                          Use {money(g.suggested_cost)}
                        </button>
                      ) : <span className="text-xs text-muted-foreground">-</span>}
                    </td>
                    <td className="py-1">
                      <Input type="number" step="0.01" min="0"
                        value={costs[g.barcode] ?? ''}
                        onChange={(e) => setCost(g.barcode, e.target.value)}
                        placeholder={g.suggested_cost != null ? String(g.suggested_cost) : 'Enter cost'}
                        className="h-8 w-[120px] text-xs"
                        data-testid={`review-cost-input-${g.barcode}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// Inline-editable preview of parsed invoices. Owner can fix barcode / price /
// discount / paid / outstanding / date / store directly, then re-validate.
// Nothing is written until Confirm Import.
function EditableInvoiceTable({ invoices, stores, onEditInvoice, onEditItem, disabled }) {
  const [openIdx, setOpenIdx] = useState(() => {
    // Auto-expand invoices that currently have errors so the user sees the fields to fix.
    const s = new Set();
    (invoices || []).forEach((iv, i) => { if (!iv.valid && !iv.exists) s.add(i); });
    return s;
  });
  if (!invoices || invoices.length === 0) return null;

  const toggle = (i) => setOpenIdx((prev) => {
    const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n;
  });

  const statusOf = (iv) => {
    if (iv.exists) return { label: 'DUPLICATE', cls: 'bg-amber-100 text-amber-800 border-amber-200' };
    if (!iv.valid) return { label: 'HAS ERRORS', cls: 'bg-destructive/10 text-destructive border-destructive/30' };
    return { label: 'READY', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' };
  };

  return (
    <div className="rounded-lg border" data-testid="invoice-editable-table">
      <div className="flex items-center justify-between border-b bg-muted/40 px-3 py-2 text-sm font-medium">
        <span>Review &amp; fix invoices ({invoices.length})</span>
        <span className="text-xs text-muted-foreground">Click a row to edit its items</span>
      </div>
      <div className="max-h-[520px] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-muted/60 text-left text-xs">
            <tr>
              <th className="w-8 p-2"></th>
              <th className="p-2 font-medium">Invoice</th>
              <th className="p-2 font-medium">Date</th>
              <th className="p-2 font-medium">Store</th>
              <th className="p-2 font-medium">Paid</th>
              <th className="p-2 font-medium">Outstanding</th>
              <th className="p-2 font-medium">Net</th>
              <th className="p-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((iv, i) => {
              const st = statusOf(iv);
              const open = openIdx.has(i);
              const hasError = !iv.valid && !iv.exists;
              return (
                <React.Fragment key={iv.invoice_no || i}>
                  <tr
                    className={`border-t cursor-pointer hover:bg-muted/30 ${hasError ? 'bg-destructive/5' : ''}`}
                    onClick={() => toggle(i)}
                    data-testid={`inv-edit-row-${i}`}
                  >
                    <td className="p-2 text-center">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggle(i); }}
                        className="rounded p-1 hover:bg-muted"
                        aria-label={open ? 'Collapse' : 'Expand'}
                        data-testid={`inv-edit-expand-${i}`}
                      >
                        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="p-2 font-mono text-xs">{iv.invoice_no}</td>
                    <td className="p-2" onClick={(e) => e.stopPropagation()}>
                      <Input
                        type="date"
                        value={(iv.invoice_date || '').slice(0, 10)}
                        disabled={disabled}
                        onChange={(e) => onEditInvoice(i, 'invoice_date', e.target.value)}
                        className="h-8 w-[140px] text-xs"
                        data-testid={`inv-edit-date-${i}`}
                      />
                    </td>
                    <td className="p-2" onClick={(e) => e.stopPropagation()}>
                      <Select value={iv.store_id || ''} disabled={disabled}
                        onValueChange={(v) => onEditInvoice(i, 'store_id', v)}>
                        <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`inv-edit-store-${i}`}>
                          <SelectValue placeholder="Select store" />
                        </SelectTrigger>
                        <SelectContent>
                          {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-2" onClick={(e) => e.stopPropagation()}>
                      <Input type="number" step="0.01" value={iv.paid ?? ''} disabled={disabled}
                        onChange={(e) => onEditInvoice(i, 'paid', e.target.value === '' ? null : Number(e.target.value))}
                        className="h-8 w-[100px] text-xs" data-testid={`inv-edit-paid-${i}`} />
                    </td>
                    <td className="p-2" onClick={(e) => e.stopPropagation()}>
                      <Input type="number" step="0.01" value={iv.outstanding ?? ''} disabled={disabled}
                        onChange={(e) => onEditInvoice(i, 'outstanding', e.target.value === '' ? null : Number(e.target.value))}
                        className="h-8 w-[110px] text-xs" data-testid={`inv-edit-out-${i}`} />
                    </td>
                    <td className="p-2 font-medium">{money(iv.net)}</td>
                    <td className="p-2">
                      <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold ${st.cls}`}
                        data-testid={`inv-edit-status-${i}`}>{st.label}</span>
                    </td>
                  </tr>
                  {open && (
                    <tr className="border-t bg-muted/10" data-testid={`inv-edit-detail-${i}`}>
                      <td colSpan={8} className="p-3">
                        {iv.errors?.length > 0 && (
                          <div className="mb-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive" data-testid={`inv-edit-errors-${i}`}>
                            {iv.errors.map((e, k) => <div key={k}>• {e.error}</div>)}
                          </div>
                        )}
                        <div className="mb-1 text-xs font-medium text-muted-foreground">Items — fix barcode / price / discount</div>
                        <table className="w-full text-xs">
                          <thead className="text-left text-muted-foreground">
                            <tr>
                              <th className="pb-1 pr-2 font-medium">Barcode</th>
                              <th className="pb-1 pr-2 font-medium">Product</th>
                              <th className="pb-1 pr-2 font-medium">Price</th>
                              <th className="pb-1 pr-2 font-medium">Discount</th>
                              <th className="pb-1 pr-2 font-medium">Issue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(iv.items || []).map((it, j) => (
                              <tr key={j} className={it.valid === false ? 'bg-destructive/5' : ''}>
                                <td className="py-1 pr-2">
                                  <Input value={it.barcode ?? ''} disabled={disabled}
                                    onChange={(e) => onEditItem(i, j, 'barcode', e.target.value)}
                                    className={`h-8 w-[150px] font-mono text-xs ${it.valid === false ? 'border-destructive' : ''}`}
                                    data-testid={`inv-edit-item-barcode-${i}-${j}`} />
                                </td>
                                <td className="py-1 pr-2 text-muted-foreground">{it.product || '—'}</td>
                                <td className="py-1 pr-2">
                                  <Input type="number" step="0.01" value={it.price ?? ''} disabled={disabled}
                                    onChange={(e) => onEditItem(i, j, 'price', e.target.value === '' ? null : Number(e.target.value))}
                                    className="h-8 w-[100px] text-xs" data-testid={`inv-edit-item-price-${i}-${j}`} />
                                </td>
                                <td className="py-1 pr-2">
                                  <Input type="number" step="0.01" value={it.discount ?? ''} disabled={disabled}
                                    onChange={(e) => onEditItem(i, j, 'discount', e.target.value === '' ? null : Number(e.target.value))}
                                    className="h-8 w-[100px] text-xs" data-testid={`inv-edit-item-disc-${i}-${j}`} />
                                </td>
                                <td className="py-1 pr-2" data-testid={`inv-edit-item-issue-${i}-${j}`}>
                                  {it.error
                                    ? <span className="flex items-center gap-1 text-destructive"><XCircle className="h-3.5 w-3.5" />{it.error}</span>
                                    : it.auto_create
                                      ? <span className="flex items-center gap-1 text-sky-600" title="This barcode is not in inventory yet - it will be created automatically on import (cost left blank for you to fill later)."><PlusCircle className="h-3.5 w-3.5" />NEW - will create</span>
                                      : <span className="text-emerald-600">OK</span>}
                                </td>
                              </tr>
                            ))}
                            {(!iv.items || iv.items.length === 0) && (
                              <tr><td colSpan={5} className="py-1 text-muted-foreground">No items parsed for this invoice.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function ImportInvoices({ stores }) {
  const [defaultStore, setDefaultStore] = useState('');
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [editInvoices, setEditInvoices] = useState(null); // working, editable copy
  const [dirty, setDirty] = useState(false);              // edits made since last validate
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reload, setReload] = useState(0);
  const [rollbackTarget, setRollbackTarget] = useState(null);

  const reset = () => { setSummary(null); setResult(null); setEditInvoices(null); setDirty(false); };

  const loadFromSummary = (data) => {
    setSummary(data);
    setEditInvoices(JSON.parse(JSON.stringify(data.invoices || [])));
    setDirty(false);
  };

  // Update a single field on an invoice (or on one of its items) in the editable copy.
  const editInvoice = (invIdx, field, value) => {
    setEditInvoices((prev) => {
      const next = prev ? [...prev] : [];
      next[invIdx] = { ...next[invIdx], [field]: value };
      return next;
    });
    setDirty(true);
  };
  const editItem = (invIdx, itemIdx, field, value) => {
    setEditInvoices((prev) => {
      const next = prev ? [...prev] : [];
      const inv = { ...next[invIdx] };
      const items = [...(inv.items || [])];
      items[itemIdx] = { ...items[itemIdx], [field]: value };
      inv.items = items;
      next[invIdx] = inv;
      return next;
    });
    setDirty(true);
  };

  const revalidate = async () => {
    if (!editInvoices) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post('/owner/invoices/import/revalidate', {
        invoices: editInvoices, filename: summary?.filename,
        batch_key: summary?.batch_key, total_rows: summary?.total_rows,
      });
      loadFromSummary(r.data.data);
      const d = r.data.data;
      if (d.invalid_invoices > 0) toast.info(`Re-checked. ${d.to_import_invoices} ready, ${d.invalid_invoices} still have errors.`);
      else toast.success(`Re-checked. All ${d.to_import_invoices} invoice(s) are valid.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-validation failed');
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/owner/invoices/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'Invoice Import Template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download template'); }
  };

  const validate = async () => {
    if (!file) return toast.error('Choose an Excel/CSV file first.');
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (defaultStore) fd.append('default_store_id', defaultStore);
      const r = await api.post('/owner/invoices/import/validate', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      loadFromSummary(r.data.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Validation failed');
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!summary || summary.to_import_invoices < 1) return;
    setBusy(true); setConfirmOpen(false);
    try {
      const r = await api.post('/owner/invoices/import/commit', {
        invoices: editInvoices || summary.invoices, filename: summary.filename,
        batch_key: summary.batch_key, total_rows: summary.total_rows,
      });
      setResult(r.data.data);
      if (r.data.data.idempotent_replay) toast.info('This file was already imported — no duplicates created.');
      else toast.success(`Imported ${r.data.data.created} invoice(s), skipped ${r.data.data.skipped}.`);
      setReload((x) => x + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  const downloadErrors = () => {
    if (!summary) return;
    const lines = [['type', 'invoice_no', 'row', 'barcode', 'error']];
    (summary.row_errors || []).forEach((e) => lines.push(['row', e.invoice_no || '', e.row || '', e.barcode || '', e.error || '']));
    (summary.invoice_errors || []).forEach((ie) => (ie.errors || []).forEach((er) => lines.push(['invoice', ie.invoice_no, er.row || '', er.barcode || '', er.error])));
    const csv = lines.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `invoice_import_errors_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const t = summary?.totals || {};
  const blocked = summary && summary.to_import_invoices < 1;

  return (
    <>
    <Card className="mt-4" data-testid="import-invoices-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" />Bulk Historical Invoice Import</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert data-testid="invoice-import-help">
          <FileSpreadsheet className="h-5 w-5" />
          <AlertTitle>How it works</AlertTitle>
          <AlertDescription className="text-xs">
            Upload XLSX/CSV. Multiple rows with the SAME <b>invoice_no</b> become ONE invoice with multiple items.
            Each item is linked to an existing garment by <b>barcode</b>. Unknown barcodes are <b>created automatically</b> in inventory (cost left blank and marked "Needs review" to fill later), so your Sales total is always preserved.
            Historical import records the past sale and does <b>not</b> change current stock. Nothing is written until you press Confirm Import.
          </AlertDescription>
        </Alert>

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px]">
            <Label>Default store (used when a row's store is blank)</Label>
            <Select value={defaultStore} onValueChange={setDefaultStore}>
              <SelectTrigger className="mt-1" data-testid="invoice-default-store"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>File (.xlsx, .xls, .csv)</Label>
            <Input type="file" accept=".xlsx,.xls,.csv" data-testid="invoice-file-input" className="mt-1"
              onChange={(e) => { setFile(e.target.files?.[0] || null); reset(); }} />
          </div>
          <Button variant="outline" onClick={downloadTemplate} data-testid="invoice-template-btn">
            <Download className="mr-2 h-4 w-4" />Template
          </Button>
          <Button onClick={validate} disabled={busy} data-testid="invoice-validate-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Validate &amp; Preview
          </Button>
        </div>

        {summary && !result && (
          <div className="space-y-3 rounded-lg border p-4" data-testid="invoice-import-summary">
            <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
              <Stat label="Excel rows" value={summary.total_rows} />
              <Stat label="Unique invoices" value={summary.unique_invoice_count} />
              <Stat label="Valid invoices" value={summary.valid_invoices} />
              <Stat label="Invalid invoices" value={summary.invalid_invoices} />
              <Stat label="Duplicate invoices" value={summary.duplicate_invoices} />
              <Stat label="Duplicate barcodes" value={summary.duplicate_barcodes_count} />
              <Stat label="Barcodes not found" value={summary.barcodes_not_found_count} />
              <Stat label="Invalid dates" value={summary.invalid_dates} />
              <Stat label="Invalid prices" value={summary.invalid_prices} />
              <Stat label="Invalid quantities" value={summary.invalid_quantities} />
              <Stat label="Payment errors" value={summary.payment_errors} />
              <Stat label="To import" value={summary.to_import_invoices} />
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm" data-testid="invoice-customer-status">
              <Stat label="Customers matched" value={summary.customers_matched} />
              <Stat label="Customers new" value={summary.customers_new} />
              <Stat label="Customers need review" value={summary.customers_needs_review} />
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-5">
              <Stat label="Total gross" value={money(t.gross)} />
              <Stat label="Total discount" value={money(t.discount)} />
              <Stat label="Total net" value={money(t.net)} />
              <Stat label="Total paid" value={money(t.paid)} />
              <Stat label="Total outstanding" value={money(t.outstanding)} />
            </div>

            {(summary.invoice_errors?.length > 0 || summary.row_errors?.length > 0) && (
              <Alert variant="destructive" data-testid="invoice-import-errors">
                <XCircle className="h-5 w-5" />
                <AlertTitle>Some rows/invoices have errors (those invoices will be skipped)</AlertTitle>
                <AlertDescription>
                  <ul className="mt-1 max-h-44 list-disc space-y-0.5 overflow-auto pl-5 text-xs">
                    {(summary.invoice_errors || []).slice(0, 40).map((ie, i) => (
                      <li key={`ie${i}`}><b>Invoice {ie.invoice_no}</b>: {(ie.errors || []).map((e) => e.error).join('; ')}</li>
                    ))}
                    {(summary.row_errors || []).slice(0, 40).map((e, i) => (
                      <li key={`re${i}`}>Row {e.row}{e.barcode ? ` (${e.barcode})` : ''}: {e.error}</li>
                    ))}
                  </ul>
                  <Button size="sm" variant="outline" className="mt-2" onClick={downloadErrors} data-testid="invoice-error-report-btn">
                    <Download className="mr-2 h-4 w-4" />Download error report (CSV)
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  Fix errors below (barcode, price, discount, paid, outstanding, date, store), then re-validate.
                  Valid, non-duplicate invoices are imported; anything still in error is skipped.
                  Unknown barcodes are created automatically (cost left blank for you to fill later in Inventory).
                </p>
                <Button size="sm" variant="secondary" onClick={revalidate} disabled={busy || !dirty}
                  data-testid="invoice-revalidate-btn">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Re-validate edits
                </Button>
              </div>
              {summary.will_create_count > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 p-2 text-xs text-sky-700" data-testid="invoice-will-create-note">
                  <PlusCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{summary.will_create_count} new item(s) with unknown barcodes will be created in inventory on import (marked "Needs review" so you can add their cost later). Your Sales total stays exactly the same.</span>
                </div>
              )}
              <EditableInvoiceTable
                invoices={editInvoices}
                stores={stores}
                disabled={busy}
                onEditInvoice={editInvoice}
                onEditItem={editItem}
              />
            </div>

            {summary.duplicate_invoices > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="invoice-dup-note">
                {summary.duplicate_invoices} invoice number(s) already exist in production and will be skipped (never overwritten).
              </p>
            )}

            {blocked ? (
              <Alert data-testid="invoice-nothing-to-import">
                <AlertTriangle className="h-5 w-5" />
                <AlertTitle>Nothing to import</AlertTitle>
                <AlertDescription>No valid, non-duplicate invoices were found. Fix the errors above and re-validate.</AlertDescription>
              </Alert>
            ) : dirty ? (
              <Alert data-testid="invoice-needs-revalidate">
                <AlertTriangle className="h-5 w-5" />
                <AlertTitle>Unsaved edits</AlertTitle>
                <AlertDescription>You changed some values. Press <b>Re-validate edits</b> to re-check before importing.</AlertDescription>
              </Alert>
            ) : (
              <Alert data-testid="invoice-ready">
                <CheckCircle2 className="h-5 w-5" />
                <AlertTitle>Ready to import</AlertTitle>
                <AlertDescription>
                  {summary.to_import_invoices} invoice(s), {summary.to_import_items} item(s), total {money(t.net)}.
                </AlertDescription>
              </Alert>
            )}

            <Button onClick={() => setConfirmOpen(true)} disabled={busy || blocked || dirty} data-testid="invoice-confirm-open-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}CONFIRM IMPORT
            </Button>
          </div>
        )}

        {result && (
          <div className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50/50 p-4" data-testid="invoice-import-result">
            <div className="flex items-center gap-2 text-emerald-800">
              <CheckCircle2 className="h-5 w-5" />
              <p className="font-semibold">{result.idempotent_replay ? 'ALREADY IMPORTED' : 'IMPORT COMPLETE'}</p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
              <Stat label="Batch ID" value={<span className="font-mono text-xs" data-testid="result-batch-id">{(result.batch_id || '').slice(0, 8) || '—'}</span>} />
              <Stat label="File" value={<span className="truncate text-xs">{result.filename || summary?.filename}</span>} />
              <Stat label="Invoices created" value={result.created} />
              <Stat label="Invoices skipped" value={result.skipped} />
              <Stat label="Rows imported" value={result.items_created} />
              <Stat label="Rows rejected" value={summary ? (summary.total_rows - result.items_created) : result.failed} />
              <Stat label="Customers matched" value={result.customers_matched ?? 0} />
              <Stat label="Customers need review" value={result.customers_needs_review ?? 0} />
              <Stat label="New items created" value={result.garments_created ?? 0} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => { setReload((x) => x + 1); document.querySelector('[data-testid="invoice-import-history"]')?.scrollIntoView({ behavior: 'smooth' }); }} data-testid="result-view-batch-btn">
                <History className="mr-2 h-4 w-4" />View Batch
              </Button>
              <Button size="sm" variant="outline" onClick={downloadErrors} disabled={!summary} data-testid="result-error-report-btn">
                <Download className="mr-2 h-4 w-4" />Download Error Report
              </Button>
              {result.batch_id && !result.idempotent_replay && (
                <Button size="sm" variant="destructive" onClick={() => setRollbackTarget({ batch_id: result.batch_id, created: result.created, filename: result.filename })} data-testid="result-rollback-btn">
                  <XCircle className="mr-2 h-4 w-4" />Rollback Batch
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => { setResult(null); setSummary(null); setFile(null); }} data-testid="result-new-import-btn">
                Start new import
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="invoice-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Invoice Import</DialogTitle>
            <DialogDescription>Please review before writing to the live database.</DialogDescription>
          </DialogHeader>
          {summary && (
            <div className="space-y-2 text-sm">
              <div className="rounded-md bg-muted/50 p-3">
                <div>Invoices to be created: <b>{summary.to_import_invoices}</b></div>
                <div>Items: <b>{summary.to_import_items}</b></div>
                <div>Total invoice value: <b>{money(summary.totals?.net)}</b></div>
                <div>Skipped (duplicates already in production): <b>{summary.duplicate_invoices}</b></div>
                <div>Invoices with errors (skipped): <b>{summary.invalid_invoices}</b></div>
                {summary.will_create_count > 0 && (
                  <div className="text-sky-700">New inventory items to auto-create: <b>{summary.will_create_count}</b> (cost blank, marked Needs review)</div>
                )}
                <div>Customers: matched <b>{summary.customers_matched}</b> · new <b>{summary.customers_new}</b> · need review <b>{summary.customers_needs_review}</b></div>
              </div>
              <p className="text-xs text-muted-foreground">
                This records historical sales only. It will NOT change current stock. Re-importing the same file will not create duplicates.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={commit} disabled={busy} data-testid="invoice-confirm-import-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Confirm &amp; Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>

    <ImportBatchHistory reload={reload} onChange={() => setReload((x) => x + 1)}
      rollbackTarget={rollbackTarget} setRollbackTarget={setRollbackTarget} />
    </>
  );
}

function ImportBatchHistory({ reload, onChange, rollbackTarget, setRollbackTarget }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setRows((await api.get('/owner/invoices/import/history')).data.data || []); }
    catch (e) { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load, reload]);

  const doRollback = async () => {
    if (!rollbackTarget) return;
    setBusy(true);
    try {
      const r = await api.post('/owner/invoices/import/rollback', { batch_id: rollbackTarget.batch_id });
      toast.success(`Rolled back — removed ${r.data.data.removed} invoice(s).`);
      setRollbackTarget(null);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rollback failed'); }
    finally { setBusy(false); }
  };

  const downloadAutoCreated = async (batchId, filename) => {
    try {
      const r = await api.get(`/owner/invoices/import/${batchId}/auto-created`);
      const gs = r.data.data.garments || [];
      if (!gs.length) { toast.info('No auto-created items in this batch.'); return; }
      const header = ['barcode', 'product', 'store_id', 'price', 'cost', 'status', 'needs_review', 'created_at'];
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = [header.join(','), ...gs.map((g) => header.map((h) => esc(g[h])).join(','))].join('\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url; a.download = `auto-created-${(filename || batchId).replace(/[^a-z0-9._-]+/gi, '_')}.csv`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`Downloaded ${gs.length} auto-created item(s).`);
    } catch (e) { toast.error('Failed to download report.'); }
  };

  return (
    <Card className="mt-4" data-testid="invoice-import-history">
      <CardHeader><CardTitle className="flex items-center gap-2"><History className="h-5 w-5" />Invoice Import History</CardTitle></CardHeader>
      <CardContent className="p-0">
        {rows === null ? (
          <div className="flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">No invoice imports yet.</div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left"><tr>
                <th className="p-2 font-medium">Batch</th><th className="p-2 font-medium">File</th>
                <th className="p-2 font-medium">When</th><th className="p-2 font-medium">User</th>
                <th className="p-2 font-medium">Created</th><th className="p-2 font-medium">Skipped</th>
                <th className="p-2 font-medium">New items</th>
                <th className="p-2 font-medium">Errors</th><th className="p-2 font-medium">Status</th>
                <th className="p-2 font-medium">Action</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t" data-testid={`batch-row-${r.batch_id || r.id}`}>
                    <td className="p-2 font-mono text-xs">{(r.batch_id || r.id || '').slice(0, 8)}</td>
                    <td className="p-2">{r.filename}</td>
                    <td className="p-2 text-xs">{new Date(r.created_at || r.ts).toLocaleString()}</td>
                    <td className="p-2">{r.created_by || r.by}</td>
                    <td className="p-2">{r.created_count ?? r.created}</td>
                    <td className="p-2">{r.skipped_count ?? r.skipped}</td>
                    <td className="p-2">{r.garments_created ?? 0}</td>
                    <td className="p-2">{r.error_count}</td>
                    <td className="p-2"><Badge variant={r.status === 'ROLLED_BACK' ? 'outline' : 'secondary'}>{r.status || 'COMPLETED'}</Badge></td>
                    <td className="p-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {(r.garments_created ?? 0) > 0 && r.status !== 'ROLLED_BACK' && (
                          <Button size="sm" variant="outline" data-testid={`report-btn-${r.batch_id || r.id}`}
                            onClick={() => downloadAutoCreated(r.batch_id || r.id, r.filename)}>
                            <Download className="mr-1 h-3.5 w-3.5" />Report
                          </Button>
                        )}
                        {r.status === 'ROLLED_BACK' || (r.created_count ?? r.created) < 1 ? (
                          !((r.garments_created ?? 0) > 0 && r.status !== 'ROLLED_BACK') && <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          <Button size="sm" variant="destructive" data-testid={`rollback-btn-${r.batch_id || r.id}`}
                            onClick={() => setRollbackTarget({ batch_id: r.batch_id || r.id, created: r.created_count ?? r.created, filename: r.filename })}>
                            Rollback Batch
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={!!rollbackTarget} onOpenChange={(o) => !o && setRollbackTarget(null)}>
        <DialogContent data-testid="rollback-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">Rollback Import Batch</DialogTitle>
            <DialogDescription>
              You are about to remove invoices created by this import batch. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {rollbackTarget && (
            <div className="rounded-md bg-muted/50 p-3 text-sm">
              <div>Batch: <b className="font-mono text-xs">{(rollbackTarget.batch_id || '').slice(0, 8)}</b></div>
              <div>File: <b>{rollbackTarget.filename}</b></div>
              <div>Invoices to remove: <b>{rollbackTarget.created}</b></div>
              <p className="mt-2 text-xs text-muted-foreground">
                Only invoices from this batch are removed. Inventory/stock, other invoices, purchases, expenses, transfers, customers and suppliers are never affected.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRollbackTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doRollback} disabled={busy} data-testid="rollback-confirm-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Confirm Rollback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


function StoreData({ stores }) {
  const [dialog, setDialog] = useState(null); // {store, mode, preview}
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const openClear = async (store, mode) => {
    setPassword(''); setConfirm(''); setAck(false);
    try {
      const r = await api.post('/owner/store/clear/preview', { store_id: store.id, mode });
      setDialog({ store, mode, ...r.data.data });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load preview'); }
  };

  const execute = async () => {
    setBusy(true);
    try {
      const r = await api.post('/owner/store/clear', {
        store_id: dialog.store.id, mode: dialog.mode, password, confirm,
      });
      const d = r.data.data;
      toast.success(`Done. Deleted ${JSON.stringify(d.deleted)}. Backup: ${d.backup_ref.slice(0, 8)}`);
      setDialog(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Operation failed'); }
    finally { setBusy(false); }
  };

  return (
    <Card className="mt-4 border-destructive/40" data-testid="store-data-card">
      <CardHeader><CardTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="h-5 w-5" />Danger Zone — Store Data Management</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <Alert variant="destructive">
          <AlertDescription className="text-xs">
            These actions delete only the selected store's data. The other store, users, settings, suppliers and configuration are never touched. A backup is taken automatically before deletion.
          </AlertDescription>
        </Alert>
        {stores.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3" data-testid={`store-row-${s.id}`}>
            <div className="font-medium">{s.name}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => openClear(s, 'invoices')} data-testid={`clear-invoices-${s.id}`}>Clear Invoices Only</Button>
              <Button variant="destructive" size="sm" onClick={() => openClear(s, 'reset')} data-testid={`reset-store-${s.id}`}>Reset Store</Button>
            </div>
          </div>
        ))}
      </CardContent>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="clear-dialog">
          {dialog && (
            <>
              <DialogHeader>
                <DialogTitle className="text-destructive">
                  {dialog.mode === 'reset' ? 'Reset Store' : 'Clear Invoices'} — {dialog.store_name}
                </DialogTitle>
                <DialogDescription>This cannot be undone from the UI (a backup snapshot is saved for recovery).</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="rounded-md bg-muted/50 p-3">
                  <div className="mb-1 font-medium">Will delete:</div>
                  <ul className="list-disc pl-5">
                    {Object.entries(dialog.will_delete).map(([k, v]) => <li key={k}>{v} {k}</li>)}
                  </ul>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {dialog.mode === 'invoices' ? 'Stock/garments are preserved.' : 'Stock AND invoices reset to zero for this store.'}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Re-enter your password</Label>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="clear-password" />
                </div>
                <div className="space-y-1.5">
                  <Label>Type <code className="rounded bg-muted px-1">{dialog.confirm_phrase}</code> to confirm</Label>
                  <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="clear-confirm-phrase" />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="ack" checked={ack} onCheckedChange={setAck} data-testid="clear-ack" />
                  <Label htmlFor="ack" className="text-xs">I understand this permanently clears {dialog.store_name}'s {dialog.mode === 'reset' ? 'stock and invoices' : 'invoices'}.</Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
                <Button variant="destructive" disabled={busy || !ack || confirm !== dialog.confirm_phrase || !password}
                  onClick={execute} data-testid="clear-execute-btn">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Confirm {dialog.mode === 'reset' ? 'Reset' : 'Clear'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function SimpleTable({ endpoint, cols, testid, empty }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get(endpoint); setRows(r.data.data || []); }
    catch (e) { setRows([]); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);
  if (rows === null) return <div className="mt-4 flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  return (
    <Card className="mt-4" data-testid={testid}>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">{empty}</div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left"><tr>{cols.map((c) => <th key={c.key} className="p-2 font-medium">{c.label}</th>)}</tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t">{cols.map((c) => <td key={c.key} className="p-2">{c.render ? c.render(r) : String(r[c.key] ?? '')}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const ImportHistory = () => (
  <SimpleTable endpoint="/owner/import/history" testid="import-history-table" empty="No imports yet."
    cols={[
      { key: 'ts', label: 'When', render: (r) => new Date(r.ts).toLocaleString() },
      { key: 'filename', label: 'File' },
      { key: 'store_name', label: 'Store' },
      { key: 'records', label: 'Records' },
      { key: 'result', label: 'Result', render: (r) => `+${r.result?.inserted || 0} ins / ${r.result?.skipped || 0} skip / ${r.result?.updated || 0} upd` },
      { key: 'by', label: 'By' },
    ]} />
);

function Backups() {
  const [rows, setRows] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const r = await api.get('/owner/backups'); setRows(r.data.data || []); } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openRestore = (b) => {
    setPassword(''); setConfirm('');
    setDlg({ ...b, confirm_phrase: `RESTORE ${b.store_name.toUpperCase()}` });
  };
  const doRestore = async () => {
    setBusy(true);
    try {
      const r = await api.post('/owner/backups/restore', { backup_id: dlg.id, password, confirm });
      toast.success(`Restored ${JSON.stringify(r.data.data.restored)}`);
      setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
    finally { setBusy(false); }
  };

  if (rows === null) return <div className="mt-4 flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  return (
    <Card className="mt-4" data-testid="backups-table">
      <CardContent className="p-0">
        {rows.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No backups yet.</div> : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left"><tr>
                <th className="p-2 font-medium">When</th><th className="p-2 font-medium">Store</th>
                <th className="p-2 font-medium">Mode</th><th className="p-2 font-medium">Snapshot</th>
                <th className="p-2 font-medium">By</th><th className="p-2 font-medium">Action</th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-2">{new Date(r.ts).toLocaleString()}</td>
                    <td className="p-2">{r.store_name}</td>
                    <td className="p-2"><Badge variant="secondary">{r.mode}</Badge></td>
                    <td className="p-2 text-xs">sales {r.counts?.sales || 0}, garments {r.counts?.garments || 0}, returns {r.counts?.returns || 0}</td>
                    <td className="p-2">{r.by}</td>
                    <td className="p-2"><Button size="sm" variant="outline" onClick={() => openRestore(r)} data-testid={`restore-${r.id}`}>Restore</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="restore-dialog">
          {dlg && (<>
            <DialogHeader>
              <DialogTitle>Restore backup — {dlg.store_name}</DialogTitle>
              <DialogDescription>Insert-only & store-scoped. Existing records are skipped; the other store is never affected.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-muted/50 p-3">
                <div>Store: <b>{dlg.store_name}</b> · Mode: {dlg.mode} · {new Date(dlg.ts).toLocaleString()}</div>
                <div className="text-xs text-muted-foreground">Will restore up to: sales {dlg.counts?.sales || 0}, garments {dlg.counts?.garments || 0}, returns {dlg.counts?.returns || 0}, transfers {dlg.counts?.transfers || 0}</div>
              </div>
              <div className="space-y-1.5"><Label>Re-enter your password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="restore-password" /></div>
              <div className="space-y-1.5"><Label>Type <code className="rounded bg-muted px-1">{dlg.confirm_phrase}</code></Label>
                <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="restore-confirm-phrase" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDlg(null)}>Cancel</Button>
              <Button disabled={busy || confirm !== dlg.confirm_phrase || !password} onClick={doRestore} data-testid="restore-execute-btn">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Restore
              </Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const AuditLog = () => (
  <SimpleTable endpoint="/owner/audit" testid="audit-table" empty="No audit entries."
    cols={[
      { key: 'ts', label: 'When', render: (r) => new Date(r.ts).toLocaleString() },
      { key: 'user', label: 'User' },
      { key: 'action', label: 'Action', render: (r) => <Badge variant="secondary">{r.action}</Badge> },
      { key: 'ref_type', label: 'Entity' },
      { key: 'meta', label: 'Detail', render: (r) => <span className="text-xs text-muted-foreground">{JSON.stringify(r.meta).slice(0, 80)}</span> },
    ]} />
);
